// utils/notification.js
import { ROOT_URL } from "../api/ApiConfig";

// Convert base64 public key to Uint8Array (needed for Chrome/Firefox)
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function isPushServerUnavailable(err) {
  const message = String(err?.message || err || "").toLowerCase();
  return (
    message.includes("could not connect to push server") ||
    message.includes("registration failed") ||
    message.includes("push service error") ||
    err?.name === "AbortError"
  );
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.warn("⚠️ Push messaging is not supported in this browser.");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register("/sw.js");

    // Edge is pickier than Chrome: wait until the SW is fully controlling.
    await navigator.serviceWorker.ready;

    if (!registration.active) {
      await new Promise((resolve) => {
        const worker =
          registration.installing || registration.waiting || registration.active;
        if (!worker || worker.state === "activated") {
          resolve();
          return;
        }
        worker.addEventListener("statechange", function onStateChange(e) {
          if (e.target.state === "activated") {
            worker.removeEventListener("statechange", onStateChange);
            resolve();
          }
        });
      });
    }

    console.log("✅ Service Worker registered and active:", registration);
    return registration;
  } catch (err) {
    console.error("❌ Service Worker registration failed:", err);
    return null;
  }
}

async function createPushSubscription(registration, applicationServerKey) {
  try {
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  } catch (err) {
    // Stale/broken local subscription can block Edge; clear and retry once.
    if (!isPushServerUnavailable(err)) throw err;

    try {
      const existing = await registration.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();
    } catch (_) {
      /* ignore */
    }

    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }
}

export async function subscribeUser(registration, vapidPublicKey) {
  if (!registration?.pushManager || !vapidPublicKey) {
    return null;
  }

  const permission = await Notification.requestPermission();
  console.log("🔔 Notification permission:", permission);

  if (permission !== "granted") {
    console.warn("❌ Notifications blocked. Cleaning up subscription…");

    let endpoint = null;
    try {
      const existing = await registration.pushManager.getSubscription();
      endpoint = existing?.endpoint ?? null;
      if (existing) await existing.unsubscribe();
    } catch (_) {
      /* ignore */
    }

    try {
      await fetch(`${ROOT_URL}/co-users/remove-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clearAll: false,
          ...(endpoint ? { endpoint } : {}),
        }),
      });
    } catch (_) {
      /* ignore */
    }

    return null;
  }

  const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

  let subscription;
  try {
    subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      console.log("🆕 No subscription found → creating one");
      subscription = await createPushSubscription(
        registration,
        applicationServerKey,
      );
    } else {
      console.log("✅ Subscription already exists:", subscription);
    }
  } catch (err) {
    // Edge often fails here when WNS/push endpoint is unreachable; do not crash the app.
    if (isPushServerUnavailable(err)) {
      console.warn(
        "⚠️ Push subscription unavailable in this browser (app will keep working without web push):",
        err?.message || err,
      );
      return null;
    }
    console.error("❌ Push subscribe failed:", err);
    return null;
  }

  // Auth cookies identify the user; body is only the push subscription.
  try {
    await fetch(`${ROOT_URL}/co-users/save-subscription`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscription }),
    });
    console.log("📡 Subscription synced with backend");
  } catch (err) {
    console.error("❌ Failed to sync subscription:", err);
  }

  return subscription;
}

/**
 * Unsubscribe this browser from web push and remove it from the backend.
 * Call on logout so notifications stop after the user signs out.
 *
 * @param {{ clearAll?: boolean }} options
 * clearAll=true removes every device subscription for the user (logout-all).
 */
export async function unsubscribeUser({ clearAll = false } = {}) {
  let endpoint = null;

  try {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        endpoint = subscription.endpoint;
        const ok = await subscription.unsubscribe();
        console.log(
          ok
            ? "✅ Browser push subscription unsubscribed"
            : "⚠️ Browser push unsubscribe returned false",
        );
      }
    }
  } catch (err) {
    console.error("❌ Failed to unsubscribe browser push:", err);
  }

  try {
    await fetch(`${ROOT_URL}/co-users/remove-subscription`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clearAll: Boolean(clearAll),
        ...(endpoint && !clearAll ? { endpoint } : {}),
      }),
    });
    console.log("📡 Push subscription removed from backend");
  } catch (err) {
    console.error("❌ Failed to remove subscription from backend:", err);
  }
}
